<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { Switch } from '@/components/ui/switch'
  import InputField from '@/components/admin/form/InputField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    OrganisationsService,
    type CustomerOrganisation as Organisation,
    type PatchedCustomerOrganisationRequest
  } from '@/types/generated'

  const { t } = useTranslation({
    en: {
      lawFirmNotifications: 'Law Firm Notifications',
      phoneCallNotifications: 'Phone Call Notifications',
      clientNotifications: 'Client Notifications',
      receiveNewCaseMail: 'Receive email when a new case is created',
      receiveDocumentSignedMail: 'Receive email when a document is completed',
      receiveQuestionnaireClosedMail: 'Receive email when a questionnaire is completed',
      receiveNewCaseEmailMail: 'Receive email when a new email arrives for a case',
      receiveNewAppointmentMail: 'Receive email when a new appointment proposal is created',
      receiveClerkChangeMail: 'Receive email when a clerk is assigned',
      receiveEmailBounceNotifications: 'Receive email when an email could not be delivered',
      receiveEmailNewCall: 'Receive email when a new call comes in',
      receiveEmailForwardedCall: 'Receive email when a call is forwarded but NOT answered',
      clientNotificationsDescription:
        'Choose whether and at what interval you want to automatically remind clients by email to complete the sent documents and questionnaires. This notification is only sent if the client has not yet processed the documents or questionnaires.',
      automaticEmailRemindersActive: 'Automatic email reminders active',
      reminderDaysLabel: 'After how many days should an automated email reminder be sent?',
      maxRemindersLabel: 'Maximum number of reminders per document',
      maxRemindersHelp:
        'Each document or questionnaire can receive a maximum of this many reminder emails'
    },
    de: {
      lawFirmNotifications: 'Kanzlei Benachrichtigungen',
      phoneCallNotifications: 'Anruf Benachrichtigungen',
      clientNotifications: 'Mandanten Benachrichtigungen',
      receiveNewCaseMail: 'E-Mail erhalten, wenn eine neue Anfrage erstellt wird',
      receiveDocumentSignedMail: 'E-Mail erhalten, wenn ein Dokument abgeschlossen wird',
      receiveQuestionnaireClosedMail: 'E-Mail erhalten, wenn ein Fragebogen abgeschlossen wird',
      receiveNewCaseEmailMail: 'E-Mail erhalten, wenn eine neue E-Mail zu einer Anfrage eingeht',
      receiveNewAppointmentMail: 'E-Mail erhalten, wenn ein neuer Terminvorschlag erstellt wird',
      receiveClerkChangeMail: 'E-Mail erhalten, wenn ein Sachbearbeiter zugewiesen wird',
      receiveEmailBounceNotifications:
        'E-Mail erhalten, wenn eine E-Mail nicht zugestellt werden konnte',
      receiveEmailNewCall: 'E-Mail erhalten, wenn ein neuer Anruf eintrifft',
      receiveEmailForwardedCall:
        'E-Mail erhalten, wenn ein neuer Anruf weitergeleitet aber NICHT angenommen wird',
      clientNotificationsDescription:
        'Wählen Sie aus, ob und in welchem Intervall Sie Mandanten automatisiert per E-Mail an das Ausfüllen der versendeten Dokumente und Fragebögen erinnern möchten. Diese Benachrichtigung wird nur versendet, wenn der Mandant die Dokumente bzw. Fragebögen noch nicht bearbeitet hat.',
      automaticEmailRemindersActive: 'Automatische E-Mail Erinnerungen aktiv',
      reminderDaysLabel:
        'Nach wie vielen Tagen soll eine automatisierte E-Mail-Erinnerung versendet werden?',
      maxRemindersLabel: 'Maximale Anzahl Erinnerungen pro Dokument',
      maxRemindersHelp:
        'Jedes Dokument oder Fragebogen kann maximal so viele Erinnerungs-E-Mails erhalten'
    }
  })

  const org = ref<Organisation>()

  onMounted(async () => {
    const response = await OrganisationsService.organisationsRetrieve({
      uuid: 'current'
    })
    org.value = response as Organisation
  })

  const handleSave = async (values: Partial<Organisation>) => {
    await OrganisationsService.organisationsPartialUpdate({
      uuid: 'current',
      requestBody: values as PatchedCustomerOrganisationRequest
    })
  }

  const handleSwitchChange = (value: boolean) => {
    org.value.send_document_email_reminders = value
    handleSave({
      send_document_email_reminders: value
    })
  }
</script>
<template>
  <SettingsPage>
    <AutoSavingForm
      v-if="org"
      class=""
      @save="handleSave"
    >
      <template v-if="org.enable_cases">
        <div class="mb-3 block font-medium">{{ t('lawFirmNotifications') }}</div>
        <div class="">
          <CheckboxField
            v-model="org.receive_new_case_mail"
            name="receive_new_case_mail"
            :label="t('receiveNewCaseMail')"
          />
          <CheckboxField
            v-model="org.receive_document_signed_mail"
            name="receive_document_signed_mail"
            :label="t('receiveDocumentSignedMail')"
          />
          <CheckboxField
            v-model="org.receive_questionnaire_closed_mail"
            name="receive_questionnaire_closed_mail"
            :label="t('receiveQuestionnaireClosedMail')"
          />
          <CheckboxField
            v-model="org.receive_new_case_email_mail"
            name="receive_new_case_email_mail"
            :label="t('receiveNewCaseEmailMail')"
          />
          <CheckboxField
            v-model="org.receive_new_appointment_mail"
            name="receive_new_appointment_mail"
            :label="t('receiveNewAppointmentMail')"
          />
          <CheckboxField
            v-model="org.receive_clerk_change_mail"
            name="receive_clerk_change_mail"
            :label="t('receiveClerkChangeMail')"
          />
          <CheckboxField
            v-model="org.receive_email_bounce_notifications"
            name="receive_email_bounce_notifications"
            :label="t('receiveEmailBounceNotifications')"
          />
        </div>
      </template>

      <template v-if="org.enable_voice">
        <div
          class="mb-3 block font-medium"
          :class="{ 'mt-6': org?.enable_cases }"
        >
          {{ t('phoneCallNotifications') }}
        </div>
        <div class="">
          <CheckboxField
            v-model="org.receive_email_new_call"
            name="receive_email_new_call"
            :label="t('receiveEmailNewCall')"
          />
          <CheckboxField
            v-model="org.receive_email_forwarded_call"
            name="receive_email_forwarded_call"
            :label="t('receiveEmailForwardedCall')"
          />
        </div>
      </template>

      <template v-if="org.enable_cases">
        <div class="mb-3 mt-6 block font-medium">{{ t('clientNotifications') }}</div>
        <p class="mb-4 text-sm text-gray-600">
          {{ t('clientNotificationsDescription') }}
        </p>
        <div class="rounded-lg border border-gray-300 p-4">
          <div class="flex items-center">
            <span class="mr-2 text-sm font-medium text-gray-700">
              {{ t('automaticEmailRemindersActive') }}
            </span>
            <Switch
              v-model="org.send_document_email_reminders"
              :checked="org.send_document_email_reminders"
              class="data-[state=checked]:bg-purple-700 data-[state=unchecked]:bg-gray-200"
              @update:checked="handleSwitchChange"
            />
          </div>
          <div
            v-if="org.send_document_email_reminders"
            class="my-4 w-full border-b border-gray-200"
          ></div>
          <div
            v-if="org.send_document_email_reminders"
            class="mt-4"
          >
            <label
              for="reminder-days"
              class="mb-1 block text-sm font-medium text-gray-700"
            >
              {{ t('reminderDaysLabel') }}
            </label>
            <div class="relative w-24">
              <InputField
                id="reminder-days"
                v-model="org.send_document_email_reminders_every_x_days"
                type="number"
                name="send_document_email_reminders_every_x_days"
                class="w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500
                  focus:outline-none focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <label
              for="max-reminders"
              class="mb-1 mt-4 block text-sm font-medium text-gray-700"
            >
              {{ t('maxRemindersLabel') }}
            </label>
            <p class="mb-2 text-xs text-gray-500">
              {{ t('maxRemindersHelp') }}
            </p>
            <div class="relative w-24">
              <InputField
                id="max-reminders"
                v-model="org.max_reminders"
                type="number"
                name="max_reminders"
                min="1"
                max="20"
                class="w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500
                  focus:outline-none focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      </template>
    </AutoSavingForm>
  </SettingsPage>
</template>
